import { useState } from 'react';
import { useNavigate } from 'react-router';
import { MessageCircle, Send, Phone, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { API_CONFIG } from '../config/api';
import { toast } from 'sonner';

interface SupportMessage {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export function DriverSupportContact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SupportMessage>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const subjects = [
    { value: 'teknik', label: 'Teknik Sorun' },
    { value: 'teslimat', label: 'Teslimat Sorunu' },
    { value: 'odeme', label: 'Ödeme / Fatura' },
    { value: 'hesap', label: 'Hesap İşlemleri' },
    { value: 'diger', label: 'Diğer' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/support/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          type: 'driver',
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSuccess(true);
      } else {
        toast.error(data.message || 'Mesaj gönderilemedi');
      }
    } catch (err) {
      console.error('Support error:', err);
      toast.error('Bağlantı hatası oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="safe-area-page min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Mesajınız Gönderildi!</h2>
            <p className="text-gray-600 mb-6">
              Destek ekibimiz en kısa sürede size dönüş yapacaktır.
            </p>
            <button
              onClick={() => {
                setSuccess(false);
                setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
              }}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Yeni Mesaj Gönder
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="safe-area-page min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 p-3">
            <MessageCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-white">Destek & İletişim</h1>
          <p className="text-blue-200 text-sm">Sizeyardımcı olmak için buradayız</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ad Soyad <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Adınızı girin"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-posta <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="eposta@ornek.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="5XX XXX XX XX"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Konu</label>
            <select
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Konu seçin</option>
              {subjects.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mesajınız <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={4}
              placeholder="Sorunuzu veya talebinizi detaylı olarak yazın..."
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-blue-700 disabled:bg-blue-300"
          >
            {loading ? (
              <span>Gönderiliyor...</span>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Mesaj Gönder</span>
              </>
            )}
          </button>

          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center mb-3">Alternatif iletişim:</p>
            <div className="grid grid-cols-2 gap-2">
              <a
                href="tel:+902324200000"
                className="flex items-center justify-center gap-2 py-2 bg-gray-100 rounded-lg text-sm text-gray-700 hover:bg-gray-200"
              >
                <Phone className="w-4 h-4" />
                <span>Arama</span>
              </a>
              <a
                href="mailto:destek@yukortagim.com.tr"
                className="flex items-center justify-center gap-2 py-2 bg-gray-100 rounded-lg text-sm text-gray-700 hover:bg-gray-200"
              >
                <Mail className="w-4 h-4" />
                <span>E-posta</span>
              </a>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
